import './PercentileRankingsChart.scss';

import {
  PercentileRankingsChartConfigDto,
  PercentileRankingsChartDto,
  PercentileRankingValue,
  PercentileRankingView,
} from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { getDisplayedColumns } from 'utilities/tableHeaders';
import { Fragment, useCallback, useMemo } from 'react';
import { useChartDataDisplay, useGetUnitSystem } from 'hooks/helperHooks';
import { useTranslation } from 'react-i18next';
import { PercentileRankingConfig, percentileRankings } from 'utilities/percentileRankings';
import { Typography } from '@trackman/web-shared-components';
import ProgressBar from 'components/Basic/ProgressBar/ProgressBar';
import { getOrderedColorSet } from 'utilities/colorHelpers';

const PercentileRankingsChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<PercentileRankingsChartDto, PercentileRankingsChartConfigDto>) => {
  const displayKeys = useMemo(() => getDisplayedColumns<string>(chartConfig.visibleStats), [chartConfig.visibleStats]);
  const viewDataAs = useMemo(() => chartConfig.viewDataAs, [chartConfig.viewDataAs]);

  const simulateChartData = useCallback(
    (isLoading: boolean) => {
      function getRandomValue(isLoading: boolean): PercentileRankingValue | null {
        if (!isLoading) {
          return null;
        }

        const random = Math.random() * 100;
        return {
          percentile: random,
          value: random,
        };
      }

      const simulatedChartData = {} as Partial<PercentileRankingsChartDto>;
      displayKeys.forEach((key) => {
        simulatedChartData[key] = getRandomValue(isLoading);
      });
      return simulatedChartData;
    },
    [displayKeys]
  );

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  return (
    <div className='PercentileRankingsChart'>
      {displayKeys.map((key, i, arr) => {
        const value = chartDataDisplay[key] as PercentileRankingValue | null;
        return (
          <Fragment key={key}>
            <PercentileRankingRow key={key} value={value} label={key} viewDataAs={viewDataAs} />
            {i < arr.length - 1 && <PillIndicator key={key + 'indicator'} />}
          </Fragment>
        );
      })}
    </div>
  );
};

const PercentileRankingRow = ({
  value,
  label,
  viewDataAs,
}: {
  value: PercentileRankingValue | null;
  label: string;
  viewDataAs: PercentileRankingView;
}) => {
  const { t } = useTranslation();
  const config = percentileRankings[label] as PercentileRankingConfig;
  const unitSystem = useGetUnitSystem();

  function getFormattedValue() {
    if (viewDataAs === 'Percentiles') {
      return value?.percentile ? Math.round(value.percentile) : '-';
    }

    let suffix = '';
    if (typeof config.unitLabel === 'function') {
      suffix = ' ' + config.unitLabel(unitSystem);
    } else if (config.unitLabel) {
      suffix = ' ' + config.unitLabel;
    }

    if (value == null) {
      return '-' + suffix;
    }

    const roundedValue = config.fractionDigits !== undefined ? value.value.toFixed(config.fractionDigits) : String(value.value);
    const formattedValue = roundedValue !== '0' && !config.leadingZero ? roundedValue.replace(/^0+/, '') : roundedValue;
    return formattedValue + suffix;
  }

  return (
    <div className='PercentileRankingRow'>
      <Typography component='div' className='label'>
        {t(`short.${label}`)}
      </Typography>
      <ProgressBar percentile={value?.percentile ?? 0} backgroundColor={getOrderedColorSet(0, 100, value?.percentile ?? 0).bg} />
      <Typography component='div' className='percentile'>
        {getFormattedValue()}
      </Typography>
    </div>
  );
};

const PillIndicator = () => {
  return (
    <div className='PercentileRankingRow PercentileRankingRow-indicators'>
      <div className='label' />
      <div className='pill-indicators'>
        <div className='pill-indicator-left' />
        <div className='pill-indicator-right' />
      </div>
      <div className='percentile' />
    </div>
  );
};

export default PercentileRankingsChart;
